import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/auth/api';
import type { LoadItem } from '@breaker/shared-types';
import type {
  CreateLoadItemBody,
  UpdateLoadItemBody,
} from '@breaker/shared-schemas';

/**
 * Goods on a load. The API returns every item across all of the
 * load's stops in one call; the dispatcher groups by loadStopId
 * client-side. Mutations invalidate the whole load-items key.
 */
export function useLoadItems(fleetId: string | null, loadId: string | null) {
  return useQuery({
    queryKey: ['load-items', fleetId ?? '', loadId ?? ''] as const,
    enabled: Boolean(fleetId && loadId),
    queryFn: () =>
      api<{ items: LoadItem[] }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/items`,
      ),
    select: (data) => data.items,
    staleTime: 30_000,
  });
}

export function useCreateLoadItem(fleetId: string | null, loadId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLoadItemBody) =>
      api<{ item: LoadItem }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/items`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['load-items', fleetId ?? '', loadId ?? ''] });
    },
  });
}

export function useUpdateLoadItem(fleetId: string | null, loadId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: UpdateLoadItemBody }) =>
      api<{ item: LoadItem }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/items/${itemId}`,
        { method: 'PATCH', body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['load-items', fleetId ?? '', loadId ?? ''] });
    },
  });
}

export function useDeleteLoadItem(fleetId: string | null, loadId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      api<void>(
        `/v1/fleets/${fleetId}/loads/${loadId}/items/${itemId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['load-items', fleetId ?? '', loadId ?? ''] });
    },
  });
}
